/* =============================================================================
 * CoachesAttendingSection — "who is going to be watching" (KGH-795 / APS-255)
 * -----------------------------------------------------------------------------
 * Ryan, item 11: "Track coach tournament attendance to inform players which
 * coaches will be present. Encouraging players to send schedules to attending
 * coaches will drive significant engagement."
 *
 * This is the player's half of that. It sits on a tournament the family has
 * already put on their own schedule and answers one question: which college
 * programs have said they will be there — and then lets the player act on the
 * answer, which is the half that makes the feature worth anything.
 *
 * FOUR THINGS IT IS CAREFUL ABOUT
 *
 *  1. IT NEVER OVERSTATES. A program is listed only when a VERIFIED coach from
 *     it marked this event and chose to publish that. It is not a guess, it is
 *     not scraped, and the empty state says "no program has said yet" rather
 *     than implying nobody is coming.
 *
 *  2. IT TELLS THE FAMILY WHEN THEY ARE INVISIBLE. If the family has their
 *     tournament schedule switched off, the coaches above cannot see that this
 *     player will be there. That is the one fact that turns this list from
 *     interesting into actionable, so it is said on the spot with a link to the
 *     setting — and said again next to each send button, because that is where
 *     the family is about to act on it.
 *
 *  3. IT KNOWS WHAT IT DOES NOT KNOW. An unknown division reads "Other", never
 *     a guessed one.
 *
 *  4. IT SPENDS THE FAMILY'S FREE PROGRAMS HONESTLY. Reaching a program's staff
 *     goes through the same §1.5 unlock as everywhere else: 3 free programs for
 *     a free family, unlimited when paid. A program already unlocked is never
 *     charged twice (the client knows which ones it holds, and unlockSchool is
 *     idempotent behind it), the cost of a new one is named in a confirm before
 *     it is spent, and a family out of programs gets the upgrade modal rather
 *     than a button that fails.
 * ========================================================================== */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { AlertCircle, EyeOff, Lock, Mail, Users } from 'react-feather';
import { coachUnlocksAPI, tournamentAPI } from '../services/api';
import { analytics } from '../services/analytics';
import type { TournamentCoachProgram, TournamentGame } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../hooks/useConfirm';
import { useSubscriptionGuard } from '../hooks/useSubscriptionGuard';
import BulkEmailCoachModal from './BulkEmailCoachModal';
import { Button, Pill, Skeleton } from './ui';

/** What the composer is open about: one program and the staff it revealed. */
interface ComposeTarget {
  program: TournamentCoachProgram;
  coaches: Array<{
    coach_name: string;
    email: string;
    title?: string;
    school?: string;
    schoolLogo?: string;
    coach_id?: string;
    school_id?: string;
  }>;
}

interface CoachesAttendingSectionProps {
  tournamentId: string;
  /** Rendered above the list, so the modal does not repeat the heading. */
  title?: string;
  /** Where "change this" should send a family that is hidden from coaches. */
  onOpenPrivacy?: () => void;
  /**
   * The athlete this tournament belongs to. Passed to the unlock calls so a
   * parent acting for one of several athletes spends the right athlete's
   * programs; omitted, the server falls back to the caller's own profile.
   */
  athleteProfileId?: string;
  /**
   * The athlete's own games at this tournament. This is what "send my schedule"
   * actually sends, so with none of them the action has nothing to say and says
   * so instead of sending an empty email.
   */
  games?: TournamentGame[];
  /** Routes the family to adding a game, from the state that needs one. */
  onAddGame?: () => void;
}

/** The one place an unknown division is named. Never "Division I" as a default. */
function divisionLabel(division: string | null): string {
  const d = String(division || '').trim();
  return d || 'Other';
}

/**
 * The draft the athlete opens on. The whole point of this surface is that the
 * common case is "read it, send it", so the schedule tag and the event are
 * already in the body — the athlete's job is to add a sentence if they want
 * one, not to compose from an empty box.
 *
 * Written around the composer's send-time strip, which drops every unresolved
 * tag AND any line left standing as a bare "Label:". Two rules follow from
 * that, and breaking either one puts a hole in a coach's inbox:
 *
 *   - anything optional (grad year, position, public profile) goes on its own
 *     "Label: {{tag}}" line, so an athlete who has not filled it in loses the
 *     whole line instead of sending a label with nothing after it. That is why
 *     the sign-off reads "Grad year: 2027" and not "Class of 2027": the strip
 *     only recognises a stranded LABEL, so a profile with no graduation year
 *     would have posted a line reading "Class of" and nothing else;
 *   - no sentence of ours may end in a colon on its own line — the strip
 *     cannot tell that one apart from an empty label and would delete it even
 *     when the schedule below it is perfectly good.
 */
function scheduleDraft(eventName: string): { subject: string; body: string } {
  return {
    subject: `Schedule for ${eventName} - {{athlete.name}} {{athlete.graduationYear}} {{athlete.position}}`,
    body:
      `{{coach.name}},\n\n` +
      `I saw that {{coach.school}} will be at ${eventName}. I am {{athlete.name}} and I will be ` +
      `playing there. Here is where you can find me.\n\n` +
      `{{athlete.tournamentSchedule}}\n\n` +
      `I would be glad to have you watch a game.\n\n` +
      `Thank you,\n\n` +
      `{{athlete.name}}\n` +
      `Grad year: {{athlete.graduationYear}}\n` +
      `Position: {{athlete.position}}\n` +
      `Profile: {{athlete.profileShareLink}}\n` +
      `{{athlete.email}}\n` +
      `{{athlete.phone}}`,
  };
}

const linkButtonStyle: React.CSSProperties = {
  background: 'none', border: 0, padding: 0, font: 'inherit',
  color: 'var(--aps-action)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline',
};

interface ProgramRowProps {
  program: TournamentCoachProgram;
  /** True while THIS program's unlock is in flight. */
  busy: boolean;
  /** True while ANY unlock is in flight, or there is nothing to send. */
  disabled: boolean;
  /** Free family that has not unlocked this program yet — the tap costs one. */
  locked: boolean;
  /**
   * This program cannot see the player at the event — either the tournament
   * schedule is switched off or the profile is hidden from college coaches.
   * Which one it is belongs in the banner; at the button, the consequence is
   * the same and is what the family needs to know before they act.
   */
  hidden: boolean;
  /** ids of the notes that explain a disabled or caveated button. */
  describedBy?: string;
  error: string | null;
  onSend: () => void;
}

const ProgramRow: React.FC<ProgramRowProps> = ({
  program, busy, disabled, locked, hidden, describedBy, error, onSend,
}) => {
  const [logoOk, setLogoOk] = useState(false);
  const hiddenNoteId = useId();

  useEffect(() => {
    if (!program.logo_url) return undefined;
    let alive = true;
    const probe = new Image();
    probe.onload = () => { if (alive) setLogoOk(true); };
    probe.onerror = () => { if (alive) setLogoOk(false); };
    probe.src = program.logo_url;
    return () => { alive = false; };
  }, [program.logo_url]);

  const initials = (program.school_name || '')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  const describe = [describedBy, hidden ? hiddenNoteId : null].filter(Boolean).join(' ');

  return (
    <li
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0',
        borderTop: '1px solid var(--aps-border)', flexWrap: 'wrap',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 36, height: 36, flexShrink: 0, borderRadius: 'var(--aps-r-sm)',
          background: logoOk ? `center / contain no-repeat url("${program.logo_url}")` : 'var(--aps-fill)',
          display: 'grid', placeItems: 'center',
          fontSize: 12, fontWeight: 800, color: 'var(--aps-text-muted)',
        }}
      >
        {logoOk ? '' : initials}
      </span>
      {/* flex-basis, not flex:1 — at 375 the action below needs a whole line of
          its own, and a row that only ever shrinks would squeeze the button to
          an ellipsis instead of wrapping it. */}
      <span style={{ flex: '1 1 150px', minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: 'var(--aps-ink)', overflowWrap: 'anywhere' }}>
          {program.school_name}
        </span>
        <span style={{ display: 'block', marginTop: 2, fontSize: 12.5, color: 'var(--aps-text-muted)', fontWeight: 600 }}>
          {[divisionLabel(program.division), program.conference].filter(Boolean).join(' · ')}
          {program.coach_names.length > 0 ? ` · ${program.coach_names.join(', ')}` : ''}
        </span>
      </span>

      <span style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
        <Button
          variant="primary"
          size="sm"
          onClick={onSend}
          loading={busy}
          disabled={disabled}
          leftIcon={locked ? <Lock size={14} aria-hidden /> : <Mail size={14} aria-hidden />}
          aria-label={`Send my schedule to ${program.school_name}`}
          aria-describedby={describe || undefined}
        >
          {busy ? 'Opening…' : 'Send my schedule'}
        </Button>
        {hidden && (
          /* Point of action, not only the banner below: the family is one tap
             from telling this program where to stand, and the program still
             cannot see them on the event's own list of athletes. */
          <span
            id={hiddenNoteId}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--aps-text-muted)', fontWeight: 600 }}
          >
            <EyeOff size={12} style={{ color: 'var(--aps-text-subtle)', flexShrink: 0 }} aria-hidden="true" />
            They cannot see you here
          </span>
        )}
      </span>

      {error && (
        <p
          role="alert"
          style={{ flexBasis: '100%', margin: '2px 0 0', fontSize: 13, color: 'var(--aps-text-2)', lineHeight: 1.5 }}
        >
          {error}{' '}
          <button type="button" onClick={onSend} style={linkButtonStyle}>Try again</button>
        </p>
      )}
    </li>
  );
};

const CoachesAttendingSection: React.FC<CoachesAttendingSectionProps> = ({
  tournamentId,
  title = 'College coaches going',
  onOpenPrivacy,
  athleteProfileId,
  games,
  onAddGame,
}) => {
  const { sessionToken } = useAuth();
  const { requirePaid } = useSubscriptionGuard();
  const [programs, setPrograms] = useState<TournamentCoachProgram[] | null>(null);
  const [shared, setShared] = useState(true);
  const [eventName, setEventName] = useState('');
  const [error, setError] = useState<string | null>(null);

  // §1.5 state. `unlockedSchools` is every program this athlete already holds —
  // from the server on load, plus whatever is unlocked here — and it is what
  // keeps a second tap on the same program from costing a second program.
  const [unlockStatus, setUnlockStatus] = useState<{ isPaid?: boolean; remaining?: number; limit?: number } | null>(null);
  const [unlockedSchools, setUnlockedSchools] = useState<Set<string>>(new Set());
  const [unlockingSchoolId, setUnlockingSchoolId] = useState<string | null>(null);
  const [pendingProgram, setPendingProgram] = useState<TournamentCoachProgram | null>(null);
  const [actionError, setActionError] = useState<{ schoolId: string; message: string } | null>(null);
  const [composeTarget, setComposeTarget] = useState<ComposeTarget | null>(null);

  // A double-tap resolves before React has re-rendered the disabled button, so
  // the guard that actually protects a free program is this ref, not the state
  // the button reads (audit: §1.5 unlock double-spend).
  const unlockInFlight = useRef(false);

  const noGamesNoteId = useId();
  // Held by identity while `games` itself is unchanged: it is handed to the
  // composer, which memoises its tag rail and its resolver on it, so this
  // section re-rendering for its own reasons (an unlock in flight, a row error)
  // does not rebuild either of them underneath an open composer.
  const playableGames = useMemo(() => (games || []).filter((g) => !!g && !!g.game_date), [games]);
  const hasGames = playableGames.length > 0;

  const freeLimit = unlockStatus?.limit ?? 3;
  const freeRemaining = unlockStatus?.remaining ?? freeLimit;
  const showsFreeCounter = !!unlockStatus && unlockStatus.isPaid !== true && typeof unlockStatus.remaining === 'number';

  const { ConfirmComponent: UnlockConfirm, confirm: confirmUnlock } = useConfirm({
    title: 'Unlock this program?',
    message:
      `${freeRemaining === 1 ? 'This is your last free program.' : `This uses 1 of your ${freeRemaining} remaining free programs.`} ` +
      `${pendingProgram?.school_name || 'This program'} then stays unlocked — every coach on its staff, for good.`,
    confirmText: 'Unlock and write',
    cancelText: 'Cancel',
    variant: 'info',
  });

  const load = useCallback(async () => {
    if (!sessionToken || !tournamentId) return;
    setError(null);
    try {
      const res = await tournamentAPI.getTournamentCoaches(sessionToken, tournamentId);
      if (res.success) {
        setPrograms(res.data.programs);
        setEventName(res.data.event_name || '');
        setShared(res.data.visibility.schedule_shared && res.data.visibility.coaches_allowed);
      } else {
        setError('We could not check which coaches are going.');
      }
    } catch {
      setError('We could not check which coaches are going.');
    }
  }, [sessionToken, tournamentId]);

  useEffect(() => { load(); }, [load]);

  // How many free programs this athlete has left, and which ones they already
  // hold. A failure here is deliberately silent: the family still gets the
  // button, and the server's own 402 remains the gate that actually counts.
  useEffect(() => {
    if (!sessionToken) return undefined;
    let alive = true;
    coachUnlocksAPI
      .getStatus(sessionToken, athleteProfileId || undefined)
      .then((status) => {
        if (!alive) return;
        setUnlockStatus(status);
        setUnlockedSchools(new Set((status.schoolIds || []).map(String)));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [sessionToken, athleteProfileId]);

  /**
   * Reveal the program's staff and open the composer on it. The POST runs even
   * for a program the athlete already holds, because it is also how the staff's
   * addresses come back — unlockSchool is idempotent for one already unlocked,
   * so that costs nothing.
   */
  const revealAndCompose = useCallback(async (program: TournamentCoachProgram) => {
    const schoolId = String(program.school_id);
    if (unlockInFlight.current || !sessionToken) return;
    unlockInFlight.current = true;
    setUnlockingSchoolId(schoolId);
    setActionError(null);
    try {
      const res = await coachUnlocksAPI.unlockSchool(sessionToken, schoolId, athleteProfileId || undefined);

      if (res.status === 402 || res.paywall) {
        // Out of free programs. Remember that, so the next tap goes straight to
        // the upgrade modal instead of spending another round trip to be told —
        // and drop any stale isPaid the client was carrying, because the server
        // has just said this family is not paid. Without that the counter below
        // stays hidden AND requirePaid no-ops (it runs its callback when the
        // client believes it is paid), which would leave the tap with nothing
        // at all to show for itself.
        setUnlockStatus((prev) => ({ ...(prev || {}), isPaid: false, remaining: 0 }));
        requirePaid(`Email ${program.school_name} your schedule`, () => {});
        return;
      }

      // Only a clean 200 that says `unlocked` is an unlock. A 403 or a 500 lands
      // here as a body with no coaches in it, and reading that shape as success
      // would bank a program the server never wrote — and tell the family it was
      // theirs.
      if (res.status !== 200 || !res.unlocked) {
        setActionError({ schoolId, message: 'We could not open a message to this program.' });
        return;
      }

      // The staff the unlock revealed. An EMPTY roster is not "unlocked but no
      // addresses": the server builds its coach_unlocks rows from this same
      // staff table, so a program with nobody on file had nothing inserted and
      // nothing spent. Claiming it here would skip the confirm next time and
      // charge the family for a program we had told them they already held.
      const roster = res.coaches || [];
      const staff = roster.filter((c) => c && !!c.email);
      if (roster.length === 0) {
        setActionError({
          schoolId,
          message: res.isPaid
            ? 'We do not have anyone from this program on file yet, so there is nobody to email.'
            : 'We do not have anyone from this program on file yet, so there is nobody to email — this did not use one of your free programs.',
        });
        return;
      }

      // Banked before anything else can go wrong: the program IS unlocked now,
      // and saying so is what stops the next tap from offering to spend a
      // second one for it — including the tap that retries the error below.
      setUnlockedSchools((prev) => {
        const next = new Set(prev);
        next.add(schoolId);
        return next;
      });
      if (typeof res.remaining === 'number') {
        const remaining = res.remaining;
        setUnlockStatus((prev) => (prev ? { ...prev, remaining } : prev));
      }

      if (staff.length === 0) {
        setActionError({
          schoolId,
          message:
            'This program is unlocked now, but we have no email address for anyone on its staff yet.',
        });
        return;
      }

      /*
       * The customer's own success metric for this feature is a volume:
       * "13 players contacting their target schools could generate ~800
       * schedule emails per event". Nothing counted it, so the number was
       * unanswerable. This fires when a composer is actually opened against a
       * program at an event - the moment a send becomes possible - and carries
       * the event so the per-event total is a breakdown rather than a guess.
       * The send itself is Gmail's, outside our instrumentation.
       */
      try {
        analytics.track('tournament_schedule_compose_opened', {
          tournament_id: tournamentId,
          school_id: schoolId,
          coaches: staff.length,
          games_in_schedule: playableGames.length,
        });
      } catch { /* analytics must never break a send */ }

      setComposeTarget({
        program,
        coaches: staff.map((c) => ({
          coach_name: c.coach_name || 'Coach',
          email: String(c.email),
          title: c.title || c.position || undefined,
          school: program.school_name,
          schoolLogo: program.logo_url || undefined,
          coach_id: c.production_id != null ? String(c.production_id) : undefined,
          school_id: schoolId,
        })),
      });
    } catch {
      setActionError({ schoolId, message: 'We could not open a message to this program.' });
    } finally {
      unlockInFlight.current = false;
      setUnlockingSchoolId(null);
    }
  }, [sessionToken, athleteProfileId, requirePaid, tournamentId, playableGames]);

  /**
   * The verb. Paid families and programs already unlocked go straight through;
   * a free family spending a NEW program is told what it costs first, and one
   * with none left gets the upgrade modal rather than a request that fails.
   * With the status unreadable we let the tap through — the server answers 402
   * and the branch above turns that into the same paywall.
   */
  const handleSend = useCallback(async (program: TournamentCoachProgram) => {
    const schoolId = String(program.school_id);
    if (unlockInFlight.current) return;
    setActionError(null);

    const costsAProgram =
      !!unlockStatus && unlockStatus.isPaid !== true && !unlockedSchools.has(schoolId);

    if (costsAProgram) {
      if (freeRemaining <= 0) {
        requirePaid(`Email ${program.school_name} your schedule`, () => {});
        return;
      }
      setPendingProgram(program);
      const confirmed = await confirmUnlock();
      setPendingProgram(null);
      if (!confirmed) return;
    }

    await revealAndCompose(program);
  }, [unlockStatus, unlockedSchools, freeRemaining, confirmUnlock, requirePaid, revealAndCompose]);

  const draft = scheduleDraft(eventName || 'this tournament');

  return (
    <section
      aria-label={title}
      style={{
        background: 'var(--aps-surface)', borderRadius: 12,
        border: '1px solid var(--aps-border)', padding: 20, marginBottom: 24,
      }}
    >
      <UnlockConfirm />

      {/* No wrap: at 375 a wrapping row dropped the heading below its own icon,
          which read as two separate things. The icon holds its line and the
          heading wraps beside it. */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <Users size={20} style={{ color: 'var(--aps-action)', flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--aps-ink)' }}>
          {title}
          {programs && programs.length > 0 ? ` (${programs.length})` : ''}
        </h3>
      </div>

      {programs === null && !error ? (
        <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
          {[0, 1].map((i) => <Skeleton key={i} variant="rect" height={44} radiusPx={8} aria-label="Checking which coaches are going" />)}
        </div>
      ) : error ? (
        <div style={{ marginTop: 14, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <AlertCircle size={16} style={{ color: 'var(--aps-text-subtle)', flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
          <p style={{ margin: 0, fontSize: 14, color: 'var(--aps-text-2)', lineHeight: 1.5 }}>
            {error}{' '}
            <button type="button" onClick={load} style={linkButtonStyle}>
              Try again
            </button>
          </p>
        </div>
      ) : programs && programs.length === 0 ? (
        <p style={{ margin: '12px 0 0', fontSize: 14, color: 'var(--aps-text-2)', lineHeight: 1.55 }}>
          No college program has said it is going to this one yet. When one does, it appears here
          and you will get a notification.
        </p>
      ) : (
        <>
          <p style={{ margin: '10px 0 2px', fontSize: 13.5, color: 'var(--aps-text-2)', lineHeight: 1.5 }}>
            These programs have said they will be at this tournament. Send them your game schedule
            and they know where to stand.
          </p>

          {showsFreeCounter && (
            /* The same counter the school profile carries, so a family never
               learns the size of the free tier from two different numbers. */
            <p
              style={{
                margin: '8px 0 0', padding: '8px 10px', borderRadius: 'var(--aps-r-sm)',
                fontSize: 12.5, fontWeight: 700, lineHeight: 1.45,
                background: freeRemaining > 0 ? 'var(--aps-success-tint)' : 'var(--aps-action-tint)',
                color: freeRemaining > 0 ? 'var(--aps-success-text)' : 'var(--aps-action-on-tint)',
                border: `1px solid ${freeRemaining > 0 ? 'var(--aps-success-border)' : 'var(--aps-action-border)'}`,
              }}
            >
              {freeRemaining > 0
                ? `${freeRemaining} of ${freeLimit} free programs left — sending to a program unlocks its whole staff.`
                : `You have used all ${freeLimit} free programs. Upgrade to reach the rest.`}
            </p>
          )}

          {!hasGames && (
            /* Not a dead button with no explanation: the thing that fixes it is
               one tap away and lives in this same modal. */
            <p
              id={noGamesNoteId}
              style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--aps-text-2)', lineHeight: 1.5 }}
            >
              Your games are not on here yet, so there is no schedule to send.{' '}
              {onAddGame ? (
                <button type="button" onClick={onAddGame} style={linkButtonStyle}>
                  Add a game
                </button>
              ) : (
                'Add a game below and the button turns on.'
              )}
            </p>
          )}

          <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
            {(programs || []).map((p) => {
              const schoolId = String(p.school_id);
              return (
                <ProgramRow
                  key={p.school_id}
                  program={p}
                  busy={unlockingSchoolId === schoolId}
                  disabled={!hasGames || (!!unlockingSchoolId && unlockingSchoolId !== schoolId)}
                  locked={!!unlockStatus && unlockStatus.isPaid !== true && !unlockedSchools.has(schoolId)}
                  hidden={!shared}
                  describedBy={!hasGames ? noGamesNoteId : undefined}
                  error={actionError && actionError.schoolId === schoolId ? actionError.message : null}
                  onSend={() => { handleSend(p); }}
                />
              );
            })}
          </ul>
        </>
      )}

      {programs !== null && !shared && (
        <div
          style={{
            marginTop: 14, padding: 12, borderRadius: 'var(--aps-r-md)',
            background: 'var(--aps-fill)', display: 'flex', gap: 10, alignItems: 'flex-start',
          }}
        >
          <EyeOff size={16} style={{ color: 'var(--aps-text-subtle)', flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
          <p style={{ margin: 0, fontSize: 13.5, color: 'var(--aps-text-2)', lineHeight: 1.55 }}>
            <Pill tone="neutral">Hidden</Pill>{' '}
            Your tournament schedule is switched off, so these coaches cannot see that you will be
            here.{' '}
            {onOpenPrivacy ? (
              <button type="button" onClick={onOpenPrivacy} style={linkButtonStyle}>
                Change it in privacy settings
              </button>
            ) : (
              'Turn it back on in your profile privacy settings.'
            )}
            {' '}Emailing them your schedule still reaches them either way.
          </p>
        </div>
      )}

      {/* The composer the athlete lands in, mounted only while it is open so
          each send starts on a fresh draft. BulkEmailCoachModal, not the
          per-coach CoachContactModal: the unit here is a PROGRAM — one unlock
          reveals its whole staff, and this is a bulk send to them. It is also
          one overlay rather than a shell wrapping a composer, which matters at
          the bottom of tournaments → detail → here. */}
      {composeTarget && (
        <BulkEmailCoachModal
          isOpen
          onClose={() => setComposeTarget(null)}
          coaches={composeTarget.coaches}
          eventSchedule={playableGames}
          /* Whose event this is. Without it the composer would describe the
             signed-in user's own family_members.athlete_profile_id, so a
             guardian with two players could send one child's schedule under the
             other child's name — and under the other child's Gmail connection,
             which is the athlete the §1.5 unlock was charged to anyway. */
          athleteProfileId={athleteProfileId}
          initialSubject={draft.subject}
          initialBody={draft.body}
          onRemoveCoach={(coachEmail) => {
            setComposeTarget((prev) =>
              prev ? { ...prev, coaches: prev.coaches.filter((c) => c.email !== coachEmail) } : prev
            );
          }}
        />
      )}
    </section>
  );
};

export default CoachesAttendingSection;
